import { Divider, Checkbox, type RefSelectProps, Select } from 'antd'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import { useState, forwardRef, type Ref, useMemo } from 'react'

import { type SingleOrMultipleSelectProps } from '@/components/types'
import { LABELS, DESCRIPTIONS } from '@/constants/common/message.constants'
import { useSitesWithAreaQuery } from '@/features/sites/hooks'
import { type SiteWithAreaEntity } from '@/libs/api'
import { type SiteType } from '@/libs/api/types'
import {
  useSelectExcludeNotExistValues,
  useSelectOnChangeHandler
} from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { mapToSiteFieldName } from '../SiteSelect'

import { UNSPECIFIED_SITE_ID } from './constants'

// Select の fieldNames で参照する最小フィールドセット。UNSPECIFIED_SITE_ID エントリは
// SiteWithAreaEntity の必須フィールド（code 等）を持たないため、内部では OptionItem で管理し
// Ant Design Select に渡す直前に SiteWithAreaEntity[] へキャストする。
type OptionItem = Pick<SiteWithAreaEntity, 'id' | 'name' | 'isArea'> & {
  address?: string
}

type Props = SingleOrMultipleSelectProps<number, SiteWithAreaEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  queryDisabled?: boolean
  tourId?: number
  shippingDate?: string
  siteType?: SiteType
  /** true の場合、「(エリアなし)」選択肢を先頭に追加する */
  showUnspecified?: boolean
}

/**
 * 地点 + エリアのセレクトボックス（DC紐づき）
 */
export const SiteAndAreaSelect = forwardRef(
  (
    {
      className,
      placeholder = DESCRIPTIONS.SELECT,
      mode,
      onChange,
      queryDisabled = false,
      tenantKey,
      dcId,
      value,
      tourId,
      shippingDate,
      siteType,
      showUnspecified = false,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [showAreas, setShowAreas] = useState(true)
    const [showSites, setShowSites] = useState(true)
    const [searchValue, setSearchValue] = useState('') // 検索文字列

    const selectedDcId = useAtomValue(selectedDcIdAtom)
    const { data: sites, isFetching } = useSitesWithAreaQuery({
      tenantKey,
      dcId: dcId ?? selectedDcId,
      tourId,
      shippingDate,
      siteType
    })

    // 選択可能なオプションをフィルタリング
    const filteredOptions = useMemo(() => {
      const result: OptionItem[] = []

      // 「エリアなし」選択肢を先頭に追加
      if (showUnspecified) {
        const noAreaLabel = LABELS.NO_AREA
        if (!searchValue || noAreaLabel.includes(searchValue)) {
          result.push({
            id: UNSPECIFIED_SITE_ID,
            name: noAreaLabel,
            isArea: false
          })
        }
      }

      if (!sites) return result as SiteWithAreaEntity[]

      const siteOptions = sites
        .filter((site) => {
          // チェックボックスでの表示制御
          const isVisible =
            (site.isArea && showAreas) || (!site.isArea && showSites)
          if (!isVisible) return false

          // 検索文字列によるフィルタリング
          if (!searchValue) return true
          return (
            mapToSiteFieldName(site).includes(searchValue) ||
            site.location?.includes(searchValue)
          )
        })
        .map((site) => ({
          ...site,
          name: mapToSiteFieldName(site),
          address: site.location
        }))

      return [...result, ...siteOptions] as SiteWithAreaEntity[]
    }, [sites, showAreas, showSites, searchValue, showUnspecified])

    const handleChange = useSelectOnChangeHandler({ mode, onChange })
    // UNSPECIFIED_SITE_ID(-1) は sentinel 値として常に許容する
    const excludeNotExistValues = useSelectExcludeNotExistValues(
      sites ?? [],
      handleChange,
      (val, target) => val === UNSPECIFIED_SITE_ID || val === target.id
    )

    return (
      <Select
        ref={ref}
        {...props}
        mode={mode}
        value={sites ? excludeNotExistValues(value) : value}
        fieldNames={{ label: 'name', value: 'id' }}
        optionFilterProp="name"
        searchValue={searchValue}
        onSearch={setSearchValue}
        autoClearSearchValue={false} // 検索欄の余白をクリックしたときに値が消えないための設定
        className={clsx(['min-w-[280px]', className])}
        options={filteredOptions}
        placeholder={placeholder}
        onChange={handleChange}
        disabled={queryDisabled}
        loading={isFetching}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <Divider className="mx-0 my-2" />
            <div className="flex h-8">
              <div className="flex items-center space-x-6 px-8">
                <Checkbox
                  checked={showSites}
                  onChange={(e) => setShowSites(e.target.checked)}
                >
                  {LABELS.SITE_WITHOUT_AREA}
                </Checkbox>
                <Checkbox
                  checked={showAreas}
                  onChange={(e) => setShowAreas(e.target.checked)}
                >
                  {LABELS.AREA}
                </Checkbox>
              </div>
            </div>
          </div>
        )}
      />
    )
  }
)
